import { FormInstance, InputProps } from "antd"

import React, { useEffect, useState } from "react"
import { getCountries, getCountryCallingCode, isSupportedCountry, parsePhoneNumber } from "react-phone-number-input"
import { CountrySelect } from "./CountrySelect"
import styles from "./PhoneInput.module.scss"
import PhoneInput from "react-phone-number-input"
import { formatPhoneNumberIntl } from "react-phone-number-input"
import { omit } from "lodash"

type Country = string
type CountryCode = { country: Country; code: string }

const getCode = (country: Country): CountryCode => ({
  country,
  code: "+" + getCountryCallingCode(country as any),
})

const defaultCode = {
  country: "FR",
  code: "+33",
}

// Longest calling codes first, so "+1242" wins over "+1"
const callingCodes = getCountries()
  .map((country: string) => getCode(country))
  .sort((a, b) => b.code.length - a.code.length)

// Country of a phone number, also for a number still being typed
const countryFromPhone = (phone: string): Country | undefined =>
  parsePhoneNumber(phone)?.country ||
  callingCodes.find(({ code }) => phone.startsWith(code))?.country

const supportedCode = (country?: Country): CountryCode | undefined =>
  country && isSupportedCountry(country as any) ? getCode(country) : undefined

const formatPhone = (phone: string) => {
  // phone = +3306XXXXXXXX || +3306XX || +3306
  // expected output = +336XXXXXXXX
  const formatted = formatPhoneNumberIntl(phone)
  if (!formatted) return phone
  return "+" + formatted.replace(/\D/g, "")
}

export interface OurPhoneProps extends Omit<InputProps, "onChange"> {
  initialValue?: string
  onChange?: (phone: string) => void
  formref?: FormInstance
}

const Phone: React.FC<OurPhoneProps> = (props): JSX.Element => {
  const [currentCode, setCurrentCode] = useState<CountryCode>(
    () => supportedCode(countryFromPhone((props.value as string) || "")) || defaultCode
  )
  const [value, setValue] = useState<string>(props.value as string)

  // The country is taken from the number itself when it already carries a
  // calling code, then from the form, then from the last selected one.
  // A remounted input must never fall back to France and wipe a foreign number.
  const resolveCode = (phone?: string): CountryCode => {
    const fallback =
      supportedCode(props.formref?.getFieldValue("country-phone")) || currentCode
    if (!phone || !phone.startsWith("+")) return fallback
    if (phone.startsWith(fallback.code)) return fallback
    return supportedCode(countryFromPhone(phone)) || fallback
  }

  // Keep the flag and the form in sync with the country of the current number
  const syncCode = (phone?: string): CountryCode => {
    const code = resolveCode(phone)
    if (code.country !== currentCode.country) setCurrentCode(code)
    if (
      props.formref &&
      props.formref.getFieldValue("country-phone") !== code.country
    ) {
      props.formref.setFieldsValue({ "country-phone": code.country })
    }
    return code
  }

  const handleCountryChanged = (code: CountryCode) => {
    setCurrentCode(code)
    props.onChange && props.onChange(code.code)
    // If country has changed, we reset phoneField
    props.formref &&
      props.formref?.setFieldsValue({
        [props.name as string]: code.code,
        "country-phone": code.country,
      })
  }

  const valueHasChanged = (value: string | undefined, code: string) => {
    if (!value) return code
    if (/^\d+$/.test(value)) return formatPhone(code + value) || code + value
    if (!value.startsWith(code)) return code
    return formatPhone(value) || value
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const { code } = syncCode(props.value as string)
    const formattedPhone = valueHasChanged(props.value as string, code)
    setValue(formattedPhone)
    props.onChange?.(formattedPhone)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value])

  return (
    <PhoneInput
      className={styles.PhoneInput}
      countrySelectComponent={(countryProps: {
        onChange?: (e: string | undefined) => void
      }) => {
        return (
          <CountrySelect
            disabled={props.disabled}
            value={currentCode.country}
            onChange={(country?: Country) => {
              country && handleCountryChanged(getCode(country))
            }}
          />
        )
      }}
      countryCallingCodeEditable={false}
      defaultCountry={currentCode.country as any}
      international
      {...omit(props, "formref", "onChange")}
      onChange={(phone) => {
        const { code } = syncCode(phone)
        const formattedPhone = valueHasChanged(phone, code)
        setValue(formattedPhone)
        props.onChange?.(formattedPhone)
      }}
      value={value}
    />
  )
}

export default Phone
